import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import {
  RiArrowUpSFill,
  RiArrowDownSFill,
  RiEqualLine,
} from "@remixicon/react";
import { formatNumberPct } from "utils/formatting";

interface KpiCardProps {
  title: string;
  value: number | string;
  trend: number;
  subtitle?: string;
  reverseTrend?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  trend,
  subtitle,
  reverseTrend = false,
}) => {
  const renderTrendIcon = () => {
    if (trend > 0) {
      return <RiArrowUpSFill />;
    } else if (trend < 0) {
      return <RiArrowDownSFill />;
    } else {
      return <RiEqualLine />;
    }
  };

  const getTrendColor = () => {
    if (trend > 0) {
      return reverseTrend ? "red" : "green";
    } else if (trend < 0) {
      return reverseTrend ? "green" : "red";
    } else {
      return "gray";
    }
  };

  return (
    <Card
      sx={{
        width: { xs: "100%", sm: "200px", md: "220px", lg: "250px" },
        height: { xs: "160px", sm: "180px", md: "200px" },
        margin: { xs: "2px", sm: "3px", md: "4px" },
        display: "inline-block",
        boxShadow: 3,
        borderRadius: "8px",
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          textAlign: "center",
          p: { xs: 1, sm: 1.5 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" },
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            component="div"
            sx={{
              fontWeight: "bold",
              fontSize: {
                xs: "1.25rem",
                sm: "1.5rem",
                md: "1.75rem",
                lg: "1.875rem",
              },
              lineHeight: 1.2,
            }}
          >
            {value}
          </Typography>

          {subtitle ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                mt: 0.5,
              }}
            >
              {subtitle}
            </Typography>
          ) : (
            <br />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: getTrendColor(),
            mb: 1,
          }}
        >
          {renderTrendIcon()}
          <Typography
            variant="body2"
            sx={{
              ml: 0.5,
              fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
            }}
          >
            {trend === 0 ? "0%" : formatNumberPct(Math.abs(trend), 2)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default KpiCard;
